import { useEffect, useState } from 'react';
import {
    NativeAdObj,
    AdError,
    PageType,
    DeviceType,
    DefineTagOptions,
    Status,
    Keywords,
} from '../../shared/types/ast';
import diagnostics from '../utils/diagnostics';
import { inscreen } from '../utils/lazy';

function warnIfRemoved(target: HTMLElement) {
    if ('MutationObserver' in window) {
        const observer = new MutationObserver(function (mutations) {
            const targetRemoved = mutations.some(function (mutation) {
                return Array.from(mutation.removedNodes).indexOf(target) !== -1;
            });

            if (targetRemoved) {
                diagnostics.warn(
                    `Native ad container ${target.id} removed from DOM. Impressions and clicks will not be registred`,
                );
            }
        });
        if (target.parentNode) {
            observer.observe(target.parentNode, {
                childList: true,
            });
        }
        const cleanup = () => {
            observer.disconnect();
        };
        return cleanup;
    }
    return () => {
        diagnostics.info('MutationObserver not available');
    };
}

const bannerDeviceType = (deviceType: DeviceType) => {
    switch (deviceType) {
        case 'desktop':
            return 'wde';
        case 'tablet':
            return 'wtb';
        case 'mobile':
            return 'wph';
    }
};

const loadElement = (ele: Element) => {
    const tagId = ele.getAttribute('id');
    if (tagId) {
        if (window.apntag) {
            window.apntag.anq.push(() => {
                if (window.apntag) {
                    window.apntag.showTag(tagId);
                }
            });
        }
    }
};

function recircIndex(id: string) {
    const lastNumber = /(\d+)(?!.*\d)/;
    const numberAsMatch = id.match(lastNumber);
    if (numberAsMatch) {
        const numberAsString = numberAsMatch[0];
        const parsedNumber = parseInt(numberAsString, 10);
        return parsedNumber > 3 ? 4 : parsedNumber;
    }
    return 0;
}

export function useNativeAd(
    instanceId: string,
    pageType: PageType,
    deviceType: DeviceType,
    keywords: { [key: string]: string },
) {
    const [loaded, setLoaded] = useState(false);
    const [data, setData] = useState<NativeAdObj>();
    const [error, setError] = useState<AdError>();

    useEffect(() => {
        if (pageType && deviceType && window.apntag && window.apntag.anq) {
            const index = recircIndex(instanceId);
            const devicePrefix = bannerDeviceType(deviceType);
            const formatPrefix = deviceType === 'mobile' ? 'board' : 'netboard';
            const categoryKeywords = Object.entries(keywords).reduce(
                (previousValue, [key, value]) => {
                    previousValue[`no-sno-finn-${key}`] = value;
                    return previousValue;
                },
                {} as Keywords,
            );

            window.apntag.anq.push(() => {
                const streamTag: DefineTagOptions = {
                    invCode: `no-finn-${devicePrefix}-${pageType}_recirc_${index}`,
                    sizes: [[1, 1]],
                    allowedFormats: ['native'],
                    targetId: instanceId,
                    keywords: {
                        'no-sno-finn-section': 'bap',
                        'no-sno-finn-subsection': 'bap_forsale',
                        'no-sno-adformat': [`${formatPrefix}_recirc`],
                        ...categoryKeywords,
                    },
                };
                if (window.apntag) {
                    window.apntag.defineTag(streamTag);
                    window.apntag.loadTags();

                    window.apntag.onEvent(
                        'adAvailable',
                        instanceId,
                        (adObj) => {
                            if (
                                adObj &&
                                adObj.adType === 'native' &&
                                adObj.source === 'rtb'
                            ) {
                                setData(adObj);
                                if (window.apntag) {
                                    window.apntag.attachClickTrackers(
                                        adObj,
                                        adObj.targetId,
                                    );
                                }

                                const element = document.getElementById(
                                    adObj.targetId,
                                );
                                if (element) {
                                    const cleanup = warnIfRemoved(element);
                                    inscreen(element, (ele: Element) => {
                                        cleanup();
                                        loadElement(ele);
                                    });
                                }
                            }
                            setLoaded(true);
                        },
                    );

                    window.apntag.onEvent('adLoaded', instanceId, (adObj) => {
                        if (
                            adObj &&
                            adObj.adType === 'native' &&
                            adObj.source === 'rtb'
                        ) {
                            if (window.apntag) {
                                window.apntag.fireImpressionTrackers(adObj);
                            }
                        }
                    });

                    window.apntag.onEvent('adNoBid', instanceId, () => {
                        setLoaded(true);
                    });

                    window.apntag.onEvent(
                        'adBadRequest',
                        instanceId,
                        (error) => {
                            setLoaded(true);
                            setError(error);
                        },
                    );
                }
            });
        } else {
            setLoaded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // We only want this to run once

    return { data, loaded, error };
}

export function useLoadNativeAd(
    adObj: NativeAdObj,
    status: Status = 'available',
) {
    useEffect(() => {
        if (adObj && window.apntag && window.apntag.anq) {
            window.apntag.anq.push(() => {
                if (window.apntag) {
                    window.apntag.attachClickTrackers(adObj, adObj.targetId);
                    if (status === 'loaded') {
                        window.apntag.fireImpressionTrackers(adObj);
                    } else {
                        window.apntag.onEvent(
                            'adLoaded',
                            adObj.targetId,
                            (adObj) => {
                                if (
                                    adObj &&
                                    adObj.adType === 'native' &&
                                    adObj.source === 'rtb'
                                ) {
                                    if (window.apntag) {
                                        window.apntag.fireImpressionTrackers(
                                            adObj,
                                        );
                                    }
                                }
                            },
                        );
                    }
                }

                const element = document.getElementById(adObj.targetId);
                if (element) {
                    const cleanup = warnIfRemoved(element);
                    inscreen(element, (ele: Element) => {
                        cleanup();
                        loadElement(ele);
                    });
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // We only want this to run once

    return {};
}
